import { Pencil } from 'lucide-react'

import { useModalContext } from '@/shared/contexts/Modal'
import { User } from '@/shared/types/user'
import { Box } from '@/shared/ui/Box'
import { Button } from '@/shared/ui/Button'
import { CardBody } from '@/shared/ui/Card'
import { CosmeticAvatarComposite } from '@/shared/ui/CosmeticAvatarComposite'

import { getSchoolRankingLabel } from '../../lib/getSchoolRankingLabel'

import { UserProfileEditAvatarModal } from './EditAvatarModal'
import { UserProfileTitlesModal } from './UserProfileTitlesModal'

interface UserProfileBodyProps {
  userProfile: User
  itsMe?: boolean
}

export const UserProfileBody = ({
  userProfile,
  itsMe = false
}: UserProfileBodyProps) => {
  const { openModal } = useModalContext()
  const schoolRankingLabel = getSchoolRankingLabel({
    excludeFromOfficialMetrics: userProfile?.exclude_from_official_metrics,
    schoolRanking: userProfile?.ranking?.school
  })

  const onEditAvatar = () => {
    openModal({ modal: UserProfileEditAvatarModal })
  }

  const onOpenTitles = () => {
    if (!userProfile) return

    openModal(
      { modal: UserProfileTitlesModal },
      { activeTitleName: userProfile?.title?.name }
    )
  }

  return (
    <CardBody>
      <div className="flex flex-col items-center justify-center">
        {userProfile ? (
          <div className="relative mb-6 w-[min(100%,240px)] mx-auto">
            <CosmeticAvatarComposite
              avatar={userProfile.avatar}
              frame={userProfile.frame}
              background={userProfile.background}
              size="lg"
              className="w-full"
              aria-label={userProfile.name}
            />
            {itsMe && (
              <div className="absolute bottom-3 right-3 z-10">
                <Button
                  type="button"
                  variant="primary"
                  icon
                  rounded
                  className="bg-jacaranda"
                  onClick={onEditAvatar}
                  aria-label="Editar avatar"
                >
                  <Pencil className="size-4" />
                </Button>
              </div>
            )}
          </div>
        ) : null}
        <Box className="font-semibold text-xl text-black text-center w-full shadow-md relative">
          {userProfile?.name}
        </Box>
        {userProfile?.title && (
          <div className="relative -mt-2">
            <Box className="pb-3 pt-4 px-5 rounded-b-lg font-semibold text-lg text-[#C75300] text-center">
              {userProfile?.title?.name}
            </Box>
            {itsMe && (
              <div className="absolute top-4 -right-5">
                <Button
                  type="button"
                  variant="primary"
                  icon
                  rounded
                  className="bg-jacaranda"
                  onClick={onOpenTitles}
                  aria-label="Listar títulos"
                >
                  <Pencil className="size-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {userProfile?.medals && userProfile.medals.length > 0 && (
        <Box className="flex flex-wrap justify-around gap-2 py-4">
          {userProfile.medals.map(medal => (
            <img
              key={medal.id}
              src={medal.media?.file?.url}
              alt={medal.name}
              className="size-10 lg:size-14"
            />
          ))}
        </Box>
      )}
      <div className="flex gap-4">
        <Box className="flex items-center gap-2 flex-grow-3 px-4">
          <img src="/icons/throphy.svg" alt="Ranking" className="size-8" />
          <span className="text-md font-semibold text-dark">
            {schoolRankingLabel}
          </span>
        </Box>
        <Box className="flex items-center gap-2 flex-grow-1 px-4">
          <img
            src="/icons/star.svg"
            alt="Quantidade de pontos"
            className="size-8"
          />
          <span className="text-md font-semibold text-dark">
            {userProfile?.points}
          </span>
        </Box>
      </div>
      {!!userProfile?.followers_count && (
        <Box className="text-md font-semibold text-dark px-4">
          Seguidores {userProfile?.followers_count}
        </Box>
      )}
    </CardBody>
  )
}
